import clsx from 'clsx'
import { useMemo, type ReactNode } from 'react'
import { View } from 'react-native'

import { Placeholder } from '@/src/elements/placeholder'
import type { TPublicationApi } from '@/src/features/publication/api/types/getPublication.types'
import { usePublicationRewardSection } from '@/src/features/publication/services/usePublicationRewardSection'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'

import { PublicationRandomIntention } from './PublicationRandomIntention'
import { PublicationRewardIntentionForm } from './PublicationRewardIntentionForm'
import { PublicationShareCardOnReward } from './PublicationShareCardOnReward'

type TPublicationRewardScreenContentProps = TSourceParam & {
  publication?: TPublicationApi
  isError: boolean
}

const PublicationRewardScreenLoadedContent = ({
  publication,
  source,
}: Required<Omit<TPublicationRewardScreenContentProps, 'isError'>>): ReactNode => {
  const { elementToDisplay, isLoading } = usePublicationRewardSection(publication.communityId, publication.id)
  const reverseOrder = useMemo(() => Math.random() > 0.5, [])

  return (
    <View className={clsx('flex gap-4', reverseOrder ? 'flex-col' : 'flex-col-reverse')}>
      <PublicationShareCardOnReward publication={publication} />
      {isLoading ? (
        <Placeholder className="mx-auto h-40 w-full max-w-[500px]" />
      ) : (
        <View className="mx-auto w-full max-w-[500px]">
          {elementToDisplay === 'random-intention' && (
            <PublicationRandomIntention
              testID={TEST_ID.PUBLICATION.REWARD.INTENTIONS.PRAY}
              publicationId={publication.id}
              community={publication.community}
              source={source}
            />
          )}
          {elementToDisplay === 'intention-form' && (
            <PublicationRewardIntentionForm source={source} publicationId={publication.id} />
          )}
        </View>
      )}
    </View>
  )
}

export const PublicationRewardScreenContent = ({
  publication,
  isError,
  source,
}: TPublicationRewardScreenContentProps): ReactNode => {
  if (publication) return <PublicationRewardScreenLoadedContent publication={publication} source={source} />
  if (isError) return null
  return <Placeholder className="h-40 w-full" />
}
